import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import '../styles/Navbar.css';
import logo from '../assets/prelogo1.jpeg';
import QuoteModal from './QuoteModal';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const navbarRef = useRef(null);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
    document.body.style.overflow = !isOpen ? 'hidden' : '';
  };

  const openQuoteModal = () => {
    setIsQuoteModalOpen(true);
    setIsOpen(false);
    document.body.style.overflow = '';
  };

  const closeQuoteModal = () => {
    setIsQuoteModalOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setScrolled(scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
    document.body.style.overflow = '';
  }, [location]);

  const handleLinkClick = (path, sectionId) => {
    setIsOpen(false);
    document.body.style.overflow = '';
    
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 150);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  const navLinks = [
    { id: 'home', label: 'HOME', path: '/', section: 'home', icon: 'fa-solid fa-house' },
    { id: 'about', label: 'ABOUT US', path: '/about', section: 'about', icon: 'fa-solid fa-circle-info' },
    { id: 'products', label: 'PRODUCTS', path: '/products', section: 'products', icon: 'fa-solid fa-headphones' },
    { id: 'contact', label: 'CONTACT US', path: '/contact', section: 'contact', icon: 'fa-solid fa-envelope' },
  ];

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname === path;
  };

  return (
    <>
      <nav 
        ref={navbarRef}
        className={`navbar ${scrolled ? 'navbar-scrolled' : ''}`}
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="navbar-container">
          {/* Logo */}
          <div className="navbar-logo">
            <Link to="/" className="logo-link" aria-label="Home">
              <img 
                src={logo} 
                alt="Preferred Audiology Logo" 
                className="logo-image"
                loading="lazy"
              />
            </Link>
          </div>

          {/* Desktop Menu */}
          <div className="desktop-menu">
            {navLinks.map((link) => (
              <Link
                key={link.id}
                to={link.path}
                className={`nav-link ${isActive(link.path) ? 'active' : ''}`}
                onClick={(e) => {
                  if (link.path === '/' && link.section === 'home') {
                    e.preventDefault();
                    handleLinkClick(link.path, link.section);
                  }
                }}
              >
                <i className={link.icon}></i>
                {link.label}
                <span className="nav-indicator"></span>
              </Link>
            ))}
          </div>

          {/* Quote Button - Desktop */}
          <div className="navbar-actions">
            <button 
              className="quote-button" 
              onClick={openQuoteModal}
              aria-label="Get a quote"
            >
              <span className="quote-text">GET QUOTE</span>
              <svg className="quote-icon" viewBox="0 0 24 24" fill="none">
                <path d="M5 12H19M19 12L13 6M19 12L13 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>

          {/* Mobile Menu Toggle */}
          <button 
            className={`mobile-menu-btn ${isOpen ? 'active' : ''}`} 
            onClick={toggleMenu}
            aria-label={isOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isOpen}
          >
            <div className={`hamburger ${isOpen ? 'open' : ''}`}>
              <span></span>
              <span></span>
              <span></span>
            </div>
          </button>
        </div>

        {/* Mobile Menu Overlay */}
        <div className={`mobile-menu-overlay ${isOpen ? 'active' : ''}`}>
          <div className="mobile-menu-container">
            <div className="mobile-menu-header">
              <Link to="/" onClick={() => {
                setIsOpen(false);
                document.body.style.overflow = '';
              }}>
                <img src={logo} alt="Logo" className="mobile-logo" />
              </Link>
              <button 
                className="mobile-close-btn"
                onClick={() => {
                  setIsOpen(false);
                  document.body.style.overflow = '';
                }}
                aria-label="Close menu"
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
            
            <div className="mobile-menu-links">
              {navLinks.map((link) => (
                <Link
                  key={link.id}
                  to={link.path}
                  className={`mobile-nav-link ${isActive(link.path) ? 'active' : ''}`}
                  onClick={() => {
                    setIsOpen(false);
                    document.body.style.overflow = '';
                  }}
                >
                  <i className={link.icon}></i>
                  {link.label}
                </Link>
              ))}
              
              <button
                className="mobile-nav-link quote-mobile"
                onClick={openQuoteModal}
              >
                <i className="fa-solid fa-paper-plane"></i>
                GET QUOTE
              </button>
            </div>

            <div className="mobile-menu-footer">
              <div className="mobile-contact-info">
                <a href="tel:03323240508" className="contact-item">
                  <i className="fa-solid fa-phone"></i>
                  <span>033-23240508</span>
                </a>
                <a href="https://wa.me/919748691530" className="contact-item" target="_blank" rel="noopener noreferrer">
                  <i className="fa-brands fa-whatsapp"></i>
                  <span>+91 9748691530</span>
                </a>
                <a href="mailto:info@paplhear.com" className="contact-item">
                  <i className="fa-solid fa-envelope"></i>
                  <span>info@paplhear.com</span>
                </a>
              </div>
              <div className="mobile-social-links">
                <a href="https://www.facebook.com/share/1DhfVX35Zi/" className="social-icon" aria-label="Facebook">
                  <i className="fa-brands fa-facebook-f"></i>
                </a>
                <a href="https://www.instagram.com/preferred_audiology_pvt_ltd?stkn=eGsxa2E4aWVyemJo" className="social-icon" aria-label="Instagram">
                  <i className="fa-brands fa-instagram"></i>
                </a>
                <a href="#" className="social-icon" aria-label="YouTube">
                  <i className="fa-brands fa-youtube"></i>
                </a>
                <a href="https://www.linkedin.com/company/preferred-audiology-pvt-ltd/" className="social-icon" aria-label="LinkedIn">
                  <i className="fa-brands fa-linkedin-in"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Quote Modal */}
      <QuoteModal isOpen={isQuoteModalOpen} onClose={closeQuoteModal} />
    </>
  );
};

export default Navbar;